import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  UserPlus, Plus, Loader2, Edit, Trash2, Eye, Users, Kanban,
  Mail, Phone, Linkedin, FileText, Calendar, ChevronRight, Briefcase,
  GripVertical, User, Clock
} from "lucide-react";

const ESTADOS_COLORS = {
  aplicado: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
  revision_cv: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  entrevista_rrhh: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  entrevista_tecnica: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
  entrevista_final: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
  oferta: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  contratado: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  rechazado: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  descartado: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
};

export default function ReclutamientoPage() {
  const [candidatos, setCandidatos] = useState([]);
  const [vacantes, setVacantes] = useState([]);
  const [estados, setEstados] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("candidatos");
  const [showCandidatoDialog, setShowCandidatoDialog] = useState(false);
  const [showVacanteDialog, setShowVacanteDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEntrevistaDialog, setShowEntrevistaDialog] = useState(false);
  const [selectedCandidato, setSelectedCandidato] = useState(null);
  const [selectedVacante, setSelectedVacante] = useState(null);
  const [editingCandidato, setEditingCandidato] = useState(null);
  const [editingVacante, setEditingVacante] = useState(null);
  const [saving, setSaving] = useState(false);
  const [vacanteFilter, setVacanteFilter] = useState("all");
  const [estadoFilter, setEstadoFilter] = useState("all");
  
  // Form states
  const [candidatoForm, setCandidatoForm] = useState({
    vacante_id: "",
    nombre: "",
    email: "",
    telefono: "",
    cv_url: "",
    linkedin_url: "",
    experiencia_años: 0,
    salario_esperado: null,
    notas: "",
    estado: "aplicado"
  });

  const [vacanteForm, setVacanteForm] = useState({
    titulo: "",
    departamento_id: "",
    descripcion: "",
    requisitos: "",
    tipo_contrato: "Tiempo completo",
    salario_min: null,
    salario_max: null,
    ubicacion: "",
    estado: "abierta"
  });

  const [entrevistaForm, setEntrevistaForm] = useState({
    candidato_id: "",
    fecha: "",
    hora: "",
    tipo: "presencial",
    notas: "",
    resultado: ""
  });
  
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [candRes, vacRes, estadosRes, pipeRes, deptRes] = await Promise.all([
        axios.get(`${API}/reclutamiento/candidatos`, getAuthHeaders()),
        axios.get(`${API}/reclutamiento/vacantes`, getAuthHeaders()),
        axios.get(`${API}/reclutamiento/estados`, getAuthHeaders()),
        axios.get(`${API}/reclutamiento/pipeline`, getAuthHeaders()),
        axios.get(`${API}/departamentos`, getAuthHeaders())
      ]);
      setCandidatos(candRes.data);
      setVacantes(vacRes.data);
      setEstados(estadosRes.data);
      setPipeline(pipeRes.data);
      setDepartamentos(deptRes.data);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetCandidatoForm = () => {
    setCandidatoForm({
      vacante_id: "", nombre: "", email: "", telefono: "", cv_url: "",
      linkedin_url: "", experiencia_años: 0, salario_esperado: null, notas: "", estado: "aplicado"
    });
    setEditingCandidato(null);
  };

  const resetVacanteForm = () => {
    setVacanteForm({
      titulo: "", departamento_id: "", descripcion: "", requisitos: "",
      tipo_contrato: "Tiempo completo", salario_min: null, salario_max: null, ubicacion: "", estado: "abierta"
    });
    setEditingVacante(null);
  };

  const handleSaveCandidato = async () => {
    if (!candidatoForm.nombre || !candidatoForm.email) {
      toast.error("Nombre y email son requeridos");
      return;
    }
    setSaving(true);
    try {
      if (editingCandidato) {
        await axios.put(`${API}/reclutamiento/candidatos/${editingCandidato.id}`, candidatoForm, getAuthHeaders());
        toast.success("Candidato actualizado");
      } else {
        await axios.post(`${API}/reclutamiento/candidatos`, candidatoForm, getAuthHeaders());
        toast.success("Candidato creado");
      }
      setShowCandidatoDialog(false);
      resetCandidatoForm();
      fetchData();
    } catch (err) {
      toast.error("Error al guardar candidato");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVacante = async () => {
    if (!vacanteForm.titulo) {
      toast.error("El título es requerido");
      return;
    }
    setSaving(true);
    try {
      if (editingVacante) {
        await axios.put(`${API}/reclutamiento/vacantes/${editingVacante.id}`, vacanteForm, getAuthHeaders());
        toast.success("Vacante actualizada");
      } else {
        await axios.post(`${API}/reclutamiento/vacantes`, vacanteForm, getAuthHeaders());
        toast.success("Vacante creada");
      }
      setShowVacanteDialog(false);
      resetVacanteForm();
      fetchData();
    } catch (err) {
      toast.error("Error al guardar vacante");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCandidato = async (id) => {
    if (!confirm("¿Eliminar este candidato?")) return;
    try {
      await axios.delete(`${API}/reclutamiento/candidatos/${id}`, getAuthHeaders());
      toast.success("Candidato eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const handleDeleteVacante = async (id) => {
    if (!confirm("¿Eliminar esta vacante?")) return;
    try {
      await axios.delete(`${API}/reclutamiento/vacantes/${id}`, getAuthHeaders());
      toast.success("Vacante eliminada");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const handleChangeEstado = async (candidatoId, nuevoEstado) => {
    try {
      await axios.put(
        `${API}/reclutamiento/candidatos/${candidatoId}/estado?estado=${nuevoEstado}`,
        {},
        getAuthHeaders()
      );
      toast.success("Estado actualizado");
      fetchData();
    } catch (err) {
      toast.error("Error al cambiar estado");
    }
  };

  const handleAddEntrevista = async () => {
    if (!entrevistaForm.fecha || !entrevistaForm.hora) {
      toast.error("Fecha y hora son requeridas");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/reclutamiento/candidatos/${selectedCandidato.id}/entrevistas`,
        entrevistaForm,
        getAuthHeaders()
      );
      toast.success("Entrevista agregada");
      setShowEntrevistaDialog(false);
      setEntrevistaForm({ candidato_id: "", fecha: "", hora: "", tipo: "presencial", notas: "", resultado: "" });
      fetchData();
      // Refresh selected candidato
      const res = await axios.get(`${API}/reclutamiento/candidatos`, getAuthHeaders());
      const updated = res.data.find(c => c.id === selectedCandidato.id);
      if (updated) setSelectedCandidato(updated);
    } catch (err) {
      toast.error("Error al agregar entrevista");
    } finally {
      setSaving(false);
    }
  };

  const openEditCandidato = (candidato) => {
    setEditingCandidato(candidato);
    setCandidatoForm({
      vacante_id: candidato.vacante_id || "",
      nombre: candidato.nombre,
      email: candidato.email,
      telefono: candidato.telefono || "",
      cv_url: candidato.cv_url || "",
      linkedin_url: candidato.linkedin_url || "",
      experiencia_años: candidato.experiencia_años || 0,
      salario_esperado: candidato.salario_esperado,
      notas: candidato.notas || "",
      estado: candidato.estado
    });
    setShowCandidatoDialog(true);
  };

  const openEditVacante = (vacante) => {
    setEditingVacante(vacante);
    setVacanteForm({
      titulo: vacante.titulo,
      departamento_id: vacante.departamento_id || "",
      descripcion: vacante.descripcion || "",
      requisitos: vacante.requisitos || "",
      tipo_contrato: vacante.tipo_contrato || "Tiempo completo",
      salario_min: vacante.salario_min,
      salario_max: vacante.salario_max,
      ubicacion: vacante.ubicacion || "",
      estado: vacante.estado
    });
    setShowVacanteDialog(true);
  };

  const filteredCandidatos = candidatos.filter(c => {
    if (vacanteFilter !== "all" && c.vacante_id !== vacanteFilter) return false;
    if (estadoFilter !== "all" && c.estado !== estadoFilter) return false;
    return true;
  });

  const getEstadoLabel = (estado) => {
    const found = estados.find(e => e.value === estado);
    return found ? found.label : estado;
  };

  // Handle drag and drop for pipeline
  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // If dropped outside a droppable area
    if (!destination) return;

    // If dropped in the same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const candidatoId = draggableId;
    const newEstado = destination.droppableId;
    const oldEstado = source.droppableId;

    // Optimistic update - update local state immediately
    setPipeline(prev => {
      const newPipeline = { ...prev };
      
      // Find and remove the candidate from source
      const sourceList = [...(newPipeline[oldEstado]?.candidates || [])];
      const candidateIndex = sourceList.findIndex(c => c.id === candidatoId);
      if (candidateIndex === -1) return prev;
      
      const [movedCandidate] = sourceList.splice(candidateIndex, 1);
      movedCandidate.estado = newEstado;
      
      // Add to destination
      const destList = [...(newPipeline[newEstado]?.candidates || [])];
      destList.splice(destination.index, 0, movedCandidate);
      
      newPipeline[oldEstado] = { ...newPipeline[oldEstado], candidates: sourceList };
      newPipeline[newEstado] = { ...newPipeline[newEstado], candidates: destList };
      
      return newPipeline;
    });

    // Update in backend
    try {
      await axios.put(
        `${API}/reclutamiento/candidatos/${candidatoId}/estado?estado=${newEstado}`,
        {},
        getAuthHeaders()
      );
      toast.success(`Estado actualizado a ${getEstadoLabel(newEstado)}`);
    } catch (err) {
      toast.error("Error al actualizar estado");
      // Revert on error - fetch fresh data
      fetchData();
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Reclutamiento
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Gestión de candidatos y procesos de selección
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { resetVacanteForm(); setShowVacanteDialog(true); }} className="gap-2" data-testid="new-vacante-btn">
              <Briefcase className="w-4 h-4" /> Nueva Vacante
            </Button>
            <Button onClick={() => { resetCandidatoForm(); setShowCandidatoDialog(true); }} className="gap-2" data-testid="new-candidato-btn">
              <UserPlus className="w-4 h-4" /> Nuevo Candidato
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{candidatos.length}</p>
                  <p className="text-sm text-slate-500">Candidatos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Briefcase className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{vacantes.filter(v => v.estado === "abierta").length}</p>
                  <p className="text-sm text-slate-500">Vacantes Abiertas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {candidatos.filter(c => ["entrevista_rrhh", "entrevista_tecnica", "entrevista_final"].includes(c.estado)).length}
                  </p>
                  <p className="text-sm text-slate-500">En Entrevistas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <UserPlus className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{candidatos.filter(c => c.estado === "contratado").length}</p>
                  <p className="text-sm text-slate-500">Contratados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="candidatos" className="gap-2">
              <Users className="w-4 h-4" /> Candidatos
            </TabsTrigger>
            <TabsTrigger value="pipeline" className="gap-2">
              <Kanban className="w-4 h-4" /> Pipeline
            </TabsTrigger>
            <TabsTrigger value="vacantes" className="gap-2">
              <Briefcase className="w-4 h-4" /> Vacantes
            </TabsTrigger>
          </TabsList>

          {/* Candidatos Tab */}
          <TabsContent value="candidatos">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Lista de Candidatos</CardTitle>
                <div className="flex gap-2">
                  <Select value={vacanteFilter} onValueChange={setVacanteFilter}>
                    <SelectTrigger className="w-48" data-testid="vacante-filter">
                      <SelectValue placeholder="Filtrar por vacante" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las vacantes</SelectItem>
                      {vacantes.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.titulo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                    <SelectTrigger className="w-40" data-testid="estado-filter">
                      <SelectValue placeholder="Filtrar por estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {estados.map(e => (
                        <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  </div>
                ) : filteredCandidatos.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay candidatos</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredCandidatos.map(cand => (
                      <div
                        key={cand.id}
                        className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg"
                        data-testid={`candidato-${cand.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{cand.nombre}</p>
                            <div className="flex items-center gap-3 text-sm text-slate-500">
                              <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {cand.email}</span>
                              {cand.telefono && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {cand.telefono}</span>}
                            </div>
                            <p className="text-xs text-slate-400 mt-1">{cand.vacante_titulo}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Select value={cand.estado} onValueChange={(v) => handleChangeEstado(cand.id, v)}>
                            <SelectTrigger className={`w-40 ${ESTADOS_COLORS[cand.estado]}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {estados.map(e => (
                                <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="ghost" onClick={() => { setSelectedCandidato(cand); setShowViewDialog(true); }}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEditCandidato(cand)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteCandidato(cand.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pipeline Tab with Drag and Drop */}
          <TabsContent value="pipeline">
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="grid grid-cols-7 gap-2">
                {estados.filter(e => !["rechazado", "descartado"].includes(e.value)).map(estado => (
                  <div key={estado.value} className="min-w-0">
                    <div className={`p-2 rounded-t-lg ${ESTADOS_COLORS[estado.value]}`}>
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-medium text-xs truncate">{estado.label}</span>
                        <Badge variant="secondary" className="text-xs px-1.5 py-0">{pipeline[estado.value]?.candidates?.length || 0}</Badge>
                      </div>
                    </div>
                    <Droppable droppableId={estado.value}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`bg-slate-100 dark:bg-slate-800 rounded-b-lg p-1.5 min-h-[350px] max-h-[450px] overflow-y-auto transition-colors ${
                            snapshot.isDraggingOver ? "bg-slate-200 dark:bg-slate-700" : ""
                          }`}
                        >
                          <div className="space-y-1.5">
                            {pipeline[estado.value]?.candidates?.map((cand, index) => (
                              <Draggable key={cand.id} draggableId={cand.id} index={index}>
                                {(provided, snapshot) => (
                                  <Card 
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={`cursor-grab hover:shadow-md transition-shadow dark:bg-slate-700 ${
                                      snapshot.isDragging ? "shadow-lg ring-2 ring-blue-500 rotate-1" : ""
                                    }`}
                                    data-testid={`pipeline-card-${cand.id}`}
                                  >
                                    <CardContent className="p-2">
                                      <div className="flex items-start gap-1">
                                        <div 
                                          {...provided.dragHandleProps} 
                                          className="mt-0.5 text-slate-400 hover:text-slate-600 cursor-grab flex-shrink-0"
                                        >
                                          <GripVertical className="w-3 h-3" />
                                        </div>
                                        <div 
                                          className="flex-1 min-w-0 cursor-pointer"
                                          onClick={() => { setSelectedCandidato(cand); setShowViewDialog(true); }}
                                        >
                                          <p className="font-medium text-xs truncate">{cand.nombre}</p>
                                          <p className="text-[10px] text-slate-500 truncate mt-0.5">{cand.vacante_titulo || "Sin vacante"}</p>
                                          {cand.entrevistas?.length > 0 && (
                                            <div className="flex items-center gap-0.5 mt-1 text-[10px] text-blue-600">
                                              <Calendar className="w-2.5 h-2.5" />
                                              {cand.entrevistas.length}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        </div>
                      )}
                    </Droppable>
                  </div>
                ))}
              </div>
            </DragDropContext>
          </TabsContent>

          {/* Vacantes Tab */}
          <TabsContent value="vacantes">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg">Vacantes</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  </div>
                ) : vacantes.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay vacantes</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {vacantes.map(vac => (
                      <Card key={vac.id} className="dark:bg-slate-700/50" data-testid={`vacante-${vac.id}`}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-semibold text-lg text-slate-900 dark:text-white">{vac.titulo}</h3>
                              <p className="text-sm text-slate-500">{vac.departamento_nombre || "Sin departamento"}</p>
                            </div>
                            <Badge className={vac.estado === "abierta" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}>
                              {vac.estado === "abierta" ? "Abierta" : "Cerrada"}
                            </Badge>
                          </div>
                          <div className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
                            <p><strong>Tipo:</strong> {vac.tipo_contrato}</p>
                            {vac.ubicacion && <p><strong>Ubicación:</strong> {vac.ubicacion}</p>}
                            {(vac.salario_min || vac.salario_max) && (
                              <p><strong>Salario:</strong> {vac.salario_min || "?"} - {vac.salario_max || "?"}</p>
                            )}
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <div className="flex items-center gap-1 text-blue-600">
                              <Users className="w-4 h-4" />
                              <span className="text-sm">{vac.candidatos_count} candidatos</span>
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openEditVacante(vac)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteVacante(vac.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Candidato Dialog */}
        <Dialog open={showCandidatoDialog} onOpenChange={setShowCandidatoDialog}>
          <DialogContent className="dark:bg-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingCandidato ? "Editar Candidato" : "Nuevo Candidato"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label>Vacante</Label>
                <Select value={candidatoForm.vacante_id || "none"} onValueChange={v => setCandidatoForm({...candidatoForm, vacante_id: v === "none" ? "" : v})}>
                  <SelectTrigger data-testid="candidato-vacante">
                    <SelectValue placeholder="Seleccionar vacante" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin vacante asignada</SelectItem>
                    {vacantes.filter(v => v.estado === "abierta").map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.titulo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre *</Label>
                  <Input value={candidatoForm.nombre} onChange={e => setCandidatoForm({...candidatoForm, nombre: e.target.value})} data-testid="candidato-nombre" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={candidatoForm.email} onChange={e => setCandidatoForm({...candidatoForm, email: e.target.value})} data-testid="candidato-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Teléfono</Label>
                  <Input value={candidatoForm.telefono} onChange={e => setCandidatoForm({...candidatoForm, telefono: e.target.value})} />
                </div>
                <div>
                  <Label>Años de Experiencia</Label>
                  <Input type="number" value={candidatoForm.experiencia_años} onChange={e => setCandidatoForm({...candidatoForm, experiencia_años: parseInt(e.target.value) || 0})} />
                </div>
              </div>
              <div>
                <Label>LinkedIn URL</Label>
                <Input value={candidatoForm.linkedin_url} onChange={e => setCandidatoForm({...candidatoForm, linkedin_url: e.target.value})} placeholder="https://linkedin.com/in/..." />
              </div>
              <div>
                <Label>Salario Esperado</Label>
                <Input type="number" value={candidatoForm.salario_esperado || ""} onChange={e => setCandidatoForm({...candidatoForm, salario_esperado: parseFloat(e.target.value) || null})} />
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea value={candidatoForm.notas} onChange={e => setCandidatoForm({...candidatoForm, notas: e.target.value})} rows={3} />
              </div>
              {editingCandidato && (
                <div>
                  <Label>Estado</Label>
                  <Select value={candidatoForm.estado} onValueChange={v => setCandidatoForm({...candidatoForm, estado: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {estados.map(e => (
                        <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCandidatoDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveCandidato} disabled={saving} data-testid="save-candidato-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Vacante Dialog */}
        <Dialog open={showVacanteDialog} onOpenChange={setShowVacanteDialog}>
          <DialogContent className="dark:bg-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingVacante ? "Editar Vacante" : "Nueva Vacante"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label>Título *</Label>
                <Input value={vacanteForm.titulo} onChange={e => setVacanteForm({...vacanteForm, titulo: e.target.value})} data-testid="vacante-titulo" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Departamento</Label>
                  <Select value={vacanteForm.departamento_id} onValueChange={v => setVacanteForm({...vacanteForm, departamento_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      {departamentos.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo de Contrato</Label>
                  <Select value={vacanteForm.tipo_contrato} onValueChange={v => setVacanteForm({...vacanteForm, tipo_contrato: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Tiempo completo">Tiempo completo</SelectItem>
                      <SelectItem value="Medio tiempo">Medio tiempo</SelectItem>
                      <SelectItem value="Por proyecto">Por proyecto</SelectItem>
                      <SelectItem value="Freelance">Freelance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Ubicación</Label>
                <Input value={vacanteForm.ubicacion} onChange={e => setVacanteForm({...vacanteForm, ubicacion: e.target.value})} placeholder="Ej: Santo Domingo, Remoto" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Salario Mínimo</Label>
                  <Input type="number" value={vacanteForm.salario_min || ""} onChange={e => setVacanteForm({...vacanteForm, salario_min: parseFloat(e.target.value) || null})} />
                </div>
                <div>
                  <Label>Salario Máximo</Label>
                  <Input type="number" value={vacanteForm.salario_max || ""} onChange={e => setVacanteForm({...vacanteForm, salario_max: parseFloat(e.target.value) || null})} />
                </div>
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea value={vacanteForm.descripcion} onChange={e => setVacanteForm({...vacanteForm, descripcion: e.target.value})} rows={3} />
              </div>
              <div>
                <Label>Requisitos</Label>
                <Textarea value={vacanteForm.requisitos} onChange={e => setVacanteForm({...vacanteForm, requisitos: e.target.value})} rows={3} />
              </div>
              {editingVacante && (
                <div>
                  <Label>Estado</Label>
                  <Select value={vacanteForm.estado} onValueChange={v => setVacanteForm({...vacanteForm, estado: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="abierta">Abierta</SelectItem>
                      <SelectItem value="cerrada">Cerrada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVacanteDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveVacante} disabled={saving} data-testid="save-vacante-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Candidato Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="dark:bg-slate-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle>Detalle del Candidato</DialogTitle>
            </DialogHeader>
            {selectedCandidato && (
              <div className="space-y-4 py-4">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <User className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-white">{selectedCandidato.nombre}</h3>
                    <Badge className={ESTADOS_COLORS[selectedCandidato.estado]}>{getEstadoLabel(selectedCandidato.estado)}</Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                    <Mail className="w-4 h-4" /> {selectedCandidato.email}
                  </div>
                  {selectedCandidato.telefono && (
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <Phone className="w-4 h-4" /> {selectedCandidato.telefono}
                    </div>
                  )}
                  {selectedCandidato.linkedin_url && (
                    <div className="flex items-center gap-2">
                      <Linkedin className="w-4 h-4 text-blue-500" />
                      <a href={selectedCandidato.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                        Ver perfil
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                    <Briefcase className="w-4 h-4" /> {selectedCandidato.experiencia_años} años exp.
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm text-slate-500">Vacante</p>
                  <p className="font-medium">{selectedCandidato.vacante_titulo || "Sin vacante asignada"}</p>
                </div>

                {selectedCandidato.notas && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-slate-500">Notas</p>
                    <p>{selectedCandidato.notas}</p>
                  </div>
                )}

                {/* Entrevistas */}
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-medium">Entrevistas</p>
                    <Button size="sm" variant="outline" onClick={() => {
                      setEntrevistaForm({ ...entrevistaForm, candidato_id: selectedCandidato.id });
                      setShowEntrevistaDialog(true);
                    }} data-testid="add-entrevista-btn">
                      <Plus className="w-4 h-4 mr-1" /> Agregar
                    </Button>
                  </div>
                  {selectedCandidato.entrevistas?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedCandidato.entrevistas.map((ent, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                          <div className="flex justify-between">
                            <p className="font-medium">{ent.tipo}</p>
                            <p className="text-sm text-slate-500">{ent.fecha} - {ent.hora}</p>
                          </div>
                          <p className="text-sm text-slate-500">Por: {ent.entrevistador_nombre}</p>
                          {ent.notas && <p className="text-sm mt-1">{ent.notas}</p>}
                          {ent.resultado && <Badge className="mt-1">{ent.resultado}</Badge>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No hay entrevistas programadas</p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Entrevista Dialog */}
        <Dialog open={showEntrevistaDialog} onOpenChange={setShowEntrevistaDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Programar Entrevista</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha *</Label>
                  <Input type="date" value={entrevistaForm.fecha} onChange={e => setEntrevistaForm({...entrevistaForm, fecha: e.target.value})} data-testid="entrevista-fecha" />
                </div>
                <div>
                  <Label>Hora *</Label>
                  <Input type="time" value={entrevistaForm.hora} onChange={e => setEntrevistaForm({...entrevistaForm, hora: e.target.value})} data-testid="entrevista-hora" />
                </div>
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={entrevistaForm.tipo} onValueChange={v => setEntrevistaForm({...entrevistaForm, tipo: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="presencial">Presencial</SelectItem>
                    <SelectItem value="virtual">Virtual</SelectItem>
                    <SelectItem value="telefonica">Telefónica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea value={entrevistaForm.notas} onChange={e => setEntrevistaForm({...entrevistaForm, notas: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEntrevistaDialog(false)}>Cancelar</Button>
              <Button onClick={handleAddEntrevista} disabled={saving} data-testid="save-entrevista-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
